/**
 * Which tags a node carries and which fields that gives it (SPEC §5.2, D25). A node carries:
 * its explicit tags (`node.tags`, for pages and anything without text), every `#x` in its text,
 * and #task when its text starts with a task prefix. `[[x]]` is a reference, not a tag. A tag
 * with a schema is a type; its fields (inherited ones included) appear under its instances.
 */
import { TAG } from './builtins.ts'
import { mergedTarget } from './conflicts.ts'
import type { Graph } from './graph.ts'
import type { NodeId } from './ids.ts'
import type { Node } from './model.ts'
import { refsOf } from './refs.ts'
import { readSchema, SCHEMA_FIELD, type FieldDef, type Schema } from './schema.ts'
import { hasTaskPrefix } from './tasks.ts'
import { titleKey } from './titles.ts'

export interface TagEnv {
  readonly g: Graph
  /** Title → live node (the title index). */
  readonly resolve: (title: string) => NodeId | null
}

/** Ids of the tags in the text (`#x` refs that resolve), without explicit or implied ones. */
export function textTags(env: TagEnv, content: string): NodeId[] {
  if (!content.includes('#')) return []
  const out: NodeId[] = []
  for (const r of refsOf(content)) {
    if (r.kind !== 'tag' || r.title === null) continue
    const id = env.resolve(r.title)
    if (id && !out.includes(id)) out.push(id)
  }
  return out
}

/** Every tag the node carries, explicit ones first, each once. */
export function tagsOf(env: TagEnv, n: Node): NodeId[] {
  const out: NodeId[] = []
  const add = (id: NodeId): void => {
    if (!out.includes(id) && env.g.isLive(id)) out.push(id)
  }
  for (const t of n.tags) add(mergedTarget(env.g, t))
  for (const t of textTags(env, n.content)) add(t)
  if (hasTaskPrefix(n.content)) add(mergedTarget(env.g, TAG.task))
  return out
}

/** True if the node carries the tag (cheap pre-checks before parsing the text). */
export function hasTag(env: TagEnv, n: Node, tagId: NodeId): boolean {
  if (n.tags.some((t) => mergedTarget(env.g, t) === tagId)) return true
  if (!n.content.includes('#')) return hasTaskPrefix(n.content) && tagId === mergedTarget(env.g, TAG.task)
  if (hasTaskPrefix(n.content) && tagId === mergedTarget(env.g, TAG.task)) return true
  const tag = env.g.get(tagId)
  if (!tag?.title || !n.content.toLowerCase().includes(titleKey(tag.title).split(' ')[0]!)) return false
  return textTags(env, n.content).includes(tagId)
}

/** Parsed schemas by node record (records are immutable, so a changed tag is a new key). */
const schemas = new WeakMap<Node, Schema | null>()

export function schemaOf(g: Graph, tagId: NodeId): Schema | null {
  const t = g.get(tagId)
  if (!t || t.deletedAt !== null) return null
  let s = schemas.get(t)
  if (s === undefined) schemas.set(t, (s = readSchema(t.props[SCHEMA_FIELD])))
  return s
}

/** A tag with a schema is a type (its instances get fields). */
export const isType = (g: Graph, tagId: NodeId): boolean => schemaOf(g, tagId) !== null

/** Fields of a tag, inherited ones first (depth-first through `extends`); a child overrides by id. */
export function tagFields(g: Graph, tagId: NodeId, seen: Set<NodeId> = new Set()): FieldDef[] {
  if (seen.has(tagId)) return []
  seen.add(tagId)
  const s = schemaOf(g, tagId)
  if (!s) return []
  const byId = new Map<string, FieldDef>()
  for (const p of s.extends) for (const f of tagFields(g, mergedTarget(g, p), seen)) byId.set(f.id, f)
  for (const f of s.fields) byId.set(f.id, f)
  return [...byId.values()]
}

export interface NodeField {
  readonly tag: NodeId
  readonly field: FieldDef
}

/** The fields a node has through its types, in tag order, each field id once. */
export function fieldsOf(env: TagEnv, n: Node): NodeField[] {
  const out: NodeField[] = []
  const seen = new Set<string>()
  for (const tag of tagsOf(env, n)) {
    for (const field of tagFields(env.g, tag)) {
      if (seen.has(field.id)) continue
      seen.add(field.id)
      out.push({ tag, field })
    }
  }
  return out
}

/** Live nodes that are types (have a schema), for "ref of" pickers and the Tags list. */
export function types(g: Graph): Node[] {
  const out: Node[] = []
  for (const n of g.nodes()) if (n.deletedAt === null && n.title !== null && n.props[SCHEMA_FIELD] !== undefined) out.push(n)
  return out.sort((a, b) => a.title!.localeCompare(b.title!))
}
